import { OrderStatus } from '@prisma/client';

/**
 * Contrato dos eventos que o servidor empurra pelo socket (F7.1).
 *
 * 🔴 **D55 — push-to-refetch: o evento avisa que SUJOU, não carrega o dado.** O cliente refaz o `GET`
 * que já existe (a janela do painel, o extrato da conta na F8). Uma fonte de verdade só, e o polling de
 * backup é **o mesmo código** — evento perdido custa, no máximo, o atraso de um poll. Payload de dado
 * real só quando nascer agregado com `withRealtime`, e aí com `version` incrementada na transação.
 *
 * 🔴 **D60 — o socket é somente leitura.** Nada aqui descreve mensagem do cliente para o servidor,
 * porque não existe: a escrita é HTTP com chave de idempotência e continua sendo.
 */

/**
 * Motivo que o cliente recebe em `connect_error` quando o handshake é recusado por credencial.
 *
 * Vive aqui porque é **contrato entre as pontas**: o servidor manda, e o cliente precisa reconhecer
 * para **desligar a reconexão** em vez de insistir em rajada de handshakes recusados. Um código só para
 * todos os casos de auth — dizer qual portão falhou informaria um atacante sobre a existência do
 * usuário, da empresa ou da sessão; o motivo real fica no log do servidor.
 */
export const HANDSHAKE_UNAUTHORIZED = 'UNAUTHORIZED';

/** Nome dos eventos no wire. String literal, porque é ela que vai no `emit` e no `on`. */
export const REALTIME_EVENT = {
  /** Pedido criado ou com status alterado, qualquer que seja a origem (D63). */
  ordersChanged: 'orders.changed',

  /**
   * A conta de uma mesa mudou: pedido novo, pedido cancelado (F8) ou pedido que mudou de status (M2-D51).
   * Publicado no room da conta.
   *
   * 🔴 **Nome próprio, e não reuso de `ordersChanged`, porque o `version` do envelope tem semântica
   * DIFERENTE nos dois.** Aqui ela é a `PdvAccounts.version` — monotônica por agregado, e o
   * `withRealtime` **depende** disso (`version == última + 1`). Em `ordersChanged` ela é o instante de
   * emissão e não ordena nada, por decisão explícita. Um nome só, com o significado do campo mudando
   * conforme o canal, é armadilha pronta: o primeiro consumidor que ligasse `withRealtime` no canal do
   * painel entraria em resync permanente, e a causa ficaria a três arquivos de distância.
   */
  accountChanged: 'account.changed',

  /**
   * Os chamados de garçom abertos do painel mudaram: chamado novo, re-toque ou baixa (M2-D58). Publicado no
   * room do **painel** (`companyOrdersChannel`), com a semântica de `ordersChanged`: push-to-refetch,
   * `version` = instante da emissão.
   *
   * ⚠️ Divide o canal com `ordersChanged`, então **todo consumidor daquele canal filtra por `type`** — sem
   * o filtro, cada chamado recarregaria as colunas de pedido.
   */
  waiterCallsChanged: 'waiter-calls.changed',
} as const;

export type RealtimeEventName = (typeof REALTIME_EVENT)[keyof typeof REALTIME_EVENT];

/**
 * Envelope de todo evento.
 *
 * `version` é sequência do **servidor**, e existe para o consumidor descartar repetido e detectar buraco
 * (`withRealtime`).
 *
 * 🔴 **O significado dela depende do EVENTO, e confundir os dois é armadilha real.** Em `orders.changed`
 * (painel) ela é o instante de emissão e não ordena nada — push-to-refetch, todo evento vira reload da
 * janela. Em `account.changed` (extrato do hóspede, F8) ela é a `PdvAccounts.version`, **monotônica por
 * agregado**, incrementada na transação que regrava a conta: é dela que o `withRealtime` depende para
 * aplicar só o que for `última + 1`. Um timestamp naquele canal derruba o cliente em `onGap` a cada
 * evento — daí o teto de sanidade em `MAX_ACCOUNT_VERSION`, no listener que publica.
 */
export interface RealtimeEnvelope<T = unknown> {
  readonly type: RealtimeEventName;
  readonly version: number;
  readonly payload: T;
  /**
   * Canal em que este evento foi publicado, preenchido **pelo servidor** no `publish`.
   *
   * Não é autorização — quem decide o que um socket recebe é o room, no handshake (D59). É
   * multiplexação: um cliente mantém **um** socket e pode ter dois consumidores em canais diferentes
   * (na F8, o extrato da conta e o que mais vier), e sem o canal no envelope os dois receberiam o
   * evento um do outro. Quem monta o envelope não preenche: o `publish` já recebe o canal, e repetir
   * o valor nos dois lugares é a primeira divergência.
   */
  readonly channel?: string;
}

/**
 * Carga de `orders.changed` — o mínimo para o cliente decidir se aquilo lhe interessa.
 *
 * Sem valor, sem nome de produto, sem dado do hóspede: quem quer detalhe faz o `GET`, que passa pelo
 * guard. Um room de conta tem vários hóspedes da mesma mesa (D63) e todos têm direito ao extrato
 * inteiro, então não haveria vazamento em mandar mais — mas mandar menos mantém uma verdade só e não
 * cria um segundo formato de pedido para manter em dia.
 */
export interface OrdersChangedPayload {
  readonly orderId: string;
  readonly status: OrderStatus;
}

export type OrdersChangedEvent = RealtimeEnvelope<OrdersChangedPayload>;

/**
 * Carga de `account.changed` (F8): **vazia, de propósito** — push-to-refetch puro. O evento só diz que a
 * conta sujou; quem quer o extrato faz o `GET`, que passa pelo guard.
 *
 * 🔴 Já carregou `orderId`, e foi removido (revisão da F8): era o **ObjectId interno** chegando ao
 * aparelho do hóspede, enquanto o extrato exclui o ObjectId do wire de propósito (D75, com teste de
 * vazamento no `PublicAccountService`). O cliente nem lia o payload — o `onEvent` só dispara o reload.
 *
 * Tipo **próprio** e não reuso de `OrdersChangedPayload`: são contratos de canais diferentes, com públicos
 * diferentes (o painel do hotel e o celular do hóspede). Amarrar os dois faria um campo acrescentado para
 * o operador viajar, de graça, para o aparelho do hóspede.
 */
export type AccountChangedPayload = Record<string, never>;

export type AccountChangedEvent = RealtimeEnvelope<AccountChangedPayload>;

/**
 * Carga de `waiter-calls.changed` (M2-D58): **vazia** — push-to-refetch puro. O painel refaz o
 * `GET /waiter-calls`, que é escopado pelo token do operador.
 */
export type WaiterCallsChangedPayload = Record<string, never>;

export type WaiterCallsChangedEvent = RealtimeEnvelope<WaiterCallsChangedPayload>;
